import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import OtpInput from "react-otp-input";
import { Loader2, AlertCircle, CheckCircle, X } from "lucide-react";
import { query, collection, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { auth, dbMobile } from "../../../config/firebaseConfig";
import dbService from "../../../firebase/dbService";
import envConfig from "../../../config/envConfig";
import { updateUserDetails } from "../../../redux/features/authSlice";
import { validatePhone, formatToE164 } from "../../../assets/js/validation";
import { Button, TextInput } from "../../../components";
import { FaWhatsapp } from "react-icons/fa";

const getInitial10DigitPhone = (raw) => {
  if (!raw) return "";
  return String(raw).replace(/^\+91|^91|^\+/, "").replace(/\D/g, "").slice(-10);
};

const VerifyPhoneModal = ({ parentsDetails, onClose }) => {
  const dispatch = useDispatch();
  const [phone, setPhone] = useState(() =>
    getInitial10DigitPhone(parentsDetails?.phoneNumber || parentsDetails?.phone)
  );
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [timer, setTimer] = useState(0);
  const [role, setRole] = useState(parentsDetails?.accountType || "parent");

  useEffect(() => {
    if (parentsDetails?.accountType) {
      setRole(parentsDetails.accountType);
    }
  }, [parentsDetails?.accountType]);

  // Timer cooldown logic
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handlePhoneChange = (e) => {
    setError("");
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhone(value);
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!phone || phone.length !== 10) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }

    const e164Phone = "+91" + phone;
    setLoading(true);
    setError("");

    try {
      // Upfront duplicate check: check if phone is already linked to another account
      try {
        const { collection, query, where, getDocs } = await import("firebase/firestore");
        const currentUid = auth.currentUser?.uid || parentsDetails?.uid;

        const q1 = query(collection(dbService.db, "user"), where("phoneNumber", "==", e164Phone));
        const snap1 = await getDocs(q1);
        const otherUser1 = snap1.docs.find((d) => d.id !== currentUid);

        if (otherUser1) {
          setError("This WhatsApp number is already linked to another account. Please use a different phone number.");
          setLoading(false);
          return;
        }

        const q2 = query(collection(dbService.db, "user"), where("phone", "==", e164Phone));
        const snap2 = await getDocs(q2);
        const otherUser2 = snap2.docs.find((d) => d.id !== currentUid);

        if (otherUser2) {
          setError("This WhatsApp number is already linked to another account. Please use a different phone number.");
          setLoading(false);
          return;
        }

        const q3 = query(collection(dbService.db, "user"), where("phone", "==", phone));
        const snap3 = await getDocs(q3);
        const otherUser3 = snap3.docs.find((d) => d.id !== currentUid);

        if (otherUser3) {
          setError("This WhatsApp number is already linked to another account. Please use a different phone number.");
          setLoading(false);
          return;
        }
      } catch (checkErr) {
        console.log("Pre-check error, proceeding to backend verification:", checkErr);
      }

      const response = await fetch(
        `${envConfig.backendUrl}/sendWhatsappOtpV1`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber: e164Phone,
            email: parentsDetails?.email,
            purpose: "Verification",
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to send OTP.");
      }

      setShowOtp(true);
      setTimer(60);
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (!otp || otp.length !== 6) {
      setError("Please enter the 6-digit OTP code.");
      return;
    }

    const e164Phone = "+91" + phone;
    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${envConfig.backendUrl}/verifyWhatsappOtpV1`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber: e164Phone,
            otp: otp,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Invalid OTP.");
      }

      // OTP Verified! Link phone to Firebase Auth account
      const currentUser = auth.currentUser;
      const idToken = currentUser ? await currentUser.getIdToken(true) : "";

      if (idToken) {
        const linkResponse = await fetch(`${envConfig.backendUrl}/linkPhoneToExistingAccountWebV1`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            phoneNumber: e164Phone,
            name: parentsDetails?.name || auth?.currentUser?.displayName || undefined,
            role: role,
          }),
        });
        const linkData = await linkResponse.json();
        if (!linkResponse.ok) {
          throw new Error(linkData.message || "Failed to link phone number to account.");
        }

        if (currentUser) {
          await currentUser.reload().catch(() => null);
        }
      }

      setSuccess(true);

      // Trigger Welcome WhatsApp Message
      try {
        await fetch(`${envConfig.backendUrl}/sendWhatsAppNotificationV1`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber: e164Phone,
            templateType: "welcome",
            parameters: [parentsDetails?.name || auth?.currentUser?.displayName || "User"]
          })
        });
      } catch (e) {
        console.error("Failed to send welcome message:", e);
      }

      // Update Redux store to close modal
      setTimeout(() => {
        const reduxUpdate = {
          phoneNumber: e164Phone,
          phone: e164Phone,
          phoneVerified: true,
          accountType: role,
        };
        dispatch(updateUserDetails(reduxUpdate));
      }, 1500);

    } catch (err) {
      console.error(err);
      setError(err.message || "Incorrect OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    setShowOtp(false);
    setOtp("");
    setError("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-fade-in">
      <div className="relative w-full max-w-md bg-white rounded-2xl p-6 sm:p-8 shadow-2xl border border-gray-100 flex flex-col gap-5 animate-scale-up">
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-450 hover:text-gray-650 transition-colors"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {success ? (
          <div className="flex flex-col items-center justify-center text-center py-6 gap-4">
            <CheckCircle className="w-16 h-16 text-emerald-500 animate-bounce" />
            <h3 className="text-xl font-extrabold text-gray-900">
              Verification Successful!
            </h3>
            <p className="text-sm text-gray-600 font-medium leading-relaxed max-w-xs">
              Your phone number has been verified. Redirecting you to your dashboard...
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex flex-col items-center text-center gap-3">
              <FaWhatsapp className="w-12 h-12 text-[#25D366]" />
              <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 tracking-tight">
                Verify Your WhatsApp Number
              </h3>
              <p className="text-sm text-gray-600 font-medium leading-relaxed">
                To receive updates, test reports, and exam alerts directly on WhatsApp, please verify your number.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-2 bg-red-50 text-red-700 p-3 rounded-lg border border-red-100 text-xs font-semibold">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Step 1: Input Phone */}
            {!showOtp ? (
              <form onSubmit={handleSendOtp} className="flex flex-col gap-4">

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-700 tracking-wide flex items-center gap-1.5">
                    <FaWhatsapp className="text-[#25D366] w-4 h-4" />
                    WhatsApp Number <span className="text-[#E5383B]">*</span>
                  </label>
                  <div className="flex gap-2 items-stretch">
                    <span className="bg-gray-100 border border-gray-200 text-gray-700 font-bold px-3 flex items-center rounded-xl text-sm select-none">
                      +91
                    </span>
                    <div className="flex-grow">
                      <TextInput
                        type="tel"
                        placeholderText="Enter 10-digit number"
                        inputName="phone-number"
                        inputValue={phone}
                        handleChange={handlePhoneChange}
                        isRequired={true}
                        inputClassName="!h-[44px] !rounded-xl !bg-white !border-gray-200 focus:!border-[#6366F1] !text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-2">
                  <Button
                    version="secondary"
                    text={loading ? "Sending..." : "Send OTP"}
                    type="submit"
                    loading={loading}
                    isDisabled={phone.length !== 10 || loading}
                  />
                </div>
              </form>
            ) : (
              /* Step 2: Input OTP */
              <form onSubmit={handleVerifyOtp} className="flex flex-col gap-4">
                <div className="flex flex-col gap-3">
                  <p className="text-xs text-gray-600 font-medium">
                    Please enter the code sent to <strong>+91 *******{phone.slice(-4)}</strong>
                  </p>
                  
                  <div className="flex justify-center py-2 otp-main-card">
                    <OtpInput
                      value={otp}
                      onChange={setOtp}
                      numInputs={6}
                      renderSeparator={<span className="w-2"></span>}
                      shouldAutoFocus
                      renderInput={(props) => (
                        <input
                          {...props}
                          style={{ width: "3rem" }}
                          className="h-12 border-2 border-gray-300 rounded-lg text-center text-lg font-bold focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2.5 mt-2">
                  <Button
                    version="secondary"
                    text={loading ? "Verifying..." : "Verify OTP"}
                    type="submit"
                    loading={loading}
                    isDisabled={otp.length !== 6 || loading}
                  />
                  
                  <div className="flex justify-between items-center text-xs mt-1">
                    <button
                      type="button"
                      onClick={handleGoBack}
                      className="text-gray-500 hover:text-gray-700 font-semibold transition-colors"
                    >
                      Change Phone Number
                    </button>
                    
                    <div>
                      {timer > 0 ? (
                        <span className="text-gray-400 font-semibold">
                          Resend in {timer}s
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          className="text-blue-600 hover:text-blue-800 font-bold transition-colors"
                        >
                          Resend Code
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyPhoneModal;
